import React, { memo } from "react";
import { Box, Paper, Typography, Stack, useTheme } from "@mui/material";
import {
  Snapshot,
  Props,
} from "../TalentDashboardInterface/TalentDashboardInterface";

const PerformanceSnapshots: React.FC<Props> = ({ data, columns = 2 }) => {
  const theme = useTheme();

  // — Netflix-ish tokens (design only)
  const COLORS = {
    bg: "#0b0b0b",
    panel: "#141414",
    text: "#ffffff",
    muted: "rgba(255,255,255,0.72)",
    border: "rgba(255,255,255,0.10)",
    red: "#E50914",
    redHover: "#b20710",
  };

  return (
    <Box>
      <Typography
        variant="h6"
        fontWeight={900}
        fontFamily="Lexend"
        mb={3}
        sx={{
          color: COLORS.text,
          letterSpacing: "-0.01em",
          display: "inline-flex",
          alignItems: "center",
          gap: 1,
          "&::after": {
            content: '""',
            width: 8,
            height: 8,
            borderRadius: "50%",
            backgroundColor: COLORS.red,
            boxShadow: "0 0 12px rgba(229,9,20,0.6)",
            display: "inline-block",
          },
        }}
      >
        Performance Snapshot
      </Typography>

      <Stack direction="row" flexWrap="wrap" spacing={3} useFlexGap>
        {data.map((item, idx) => (
          <Paper
            key={idx}
            elevation={0}
            sx={{
              flex: `1 1 calc(${100 / columns}% - 24px)`,
              minWidth: 180,
              borderRadius: 3,
              px: { xs: 2, sm: 3 },
              py: { xs: 2, sm: 2.5 },
              background: `
                radial-gradient(700px 260px at 0% 0%, rgba(229,9,20,0.08), transparent 60%),
                linear-gradient(180deg, #151515, #111111)
              `,
              color: COLORS.text,
              border: `1px solid ${COLORS.border}`,
              boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              transition: "transform .25s ease, box-shadow .25s ease, border-color .25s ease",
              "&:hover": {
                transform: "translateY(-3px)",
                borderColor: "rgba(255,255,255,0.18)",
                boxShadow:
                  "0 22px 60px rgba(0,0,0,0.65), 0 0 0 1px rgba(255,255,255,0.04) inset",
              },
            }}
          >
            <Box sx={{ display: "flex", alignItems: "center", pr: 2, minWidth: 0 }}>
              <Typography
                fontSize={{ xs: 13, sm: 14 }}
                fontWeight={600}
                color={COLORS.muted}
                fontFamily="Lexend"
                noWrap
                title={item.label}
              >
                {item.label}
              </Typography>
            </Box>

            <Box display="flex" alignItems="center" gap={1.25}>
              <Typography
                fontSize={{ xs: 20, sm: 24 }}
                fontWeight={800}
                mt={0.5}
                fontFamily="Lexend"
                sx={{
                  background:
                    "linear-gradient(90deg, #FFFFFF 0%, rgba(255,255,255,0.85) 60%)",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                  textShadow: "0 1px 0 rgba(0,0,0,0.25)",
                }}
              >
                {item.value}
              </Typography>
              <Box
                fontSize={{ xs: 26, sm: 32 }}
                display="flex"
                alignItems="center"
                sx={{
                  color: item.color || COLORS.red,
                  textShadow: "0 1px 0 rgba(0,0,0,0.25)",
                }}
              >
                {item.icon}
              </Box>
            </Box>
          </Paper>
        ))}
      </Stack>
    </Box>
  );
};

export default memo(PerformanceSnapshots);
